import { createSelector } from "@reduxjs/toolkit";
import { mapKeys, merge, reduce } from "lodash";

import { IDataKeys, IJSONAPIItem } from "shared/types";
import { BAND_ACTIONS, IBandIcal, IEvent } from "Bands/types";
import { API_ENDPOINTS } from "shared/constants";
import { buildSingleItem, getIdFromPath } from "shared/utils";
import { getCalendarCacheKey } from "../utils/helpers";
import { dataSelector, buildData, dataMetaSelectorHelper } from "./helpers";

export const eventsSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.event) as IEvent[]
);

const mapIdsToEvents = (events: IEvent[], eventIds?: IJSONAPIItem[]) => {
  const eventMap = mapKeys(events, ({ id }) => id);

  const eventObjects = reduce(
    eventIds,
    (calendarEvents: IEvent[], { id, ...eventProps }) => {
      const event = merge(
        {},
        { id },
        eventProps,
        eventMap?.[getIdFromPath(id, API_ENDPOINTS.EVENTS)]
      );

      if (event) {
        return [...calendarEvents, event];
      }

      return calendarEvents;
    },
    []
  );

  // filter only events we can find
  return eventObjects.filter((event) => event.title);
};

export const calendarEventsSelectorCreator = (
  band?: string,
  start?: string,
  stop?: string
) =>
  createSelector([dataSelector, eventsSelector], (data, events) => {
    return mapIdsToEvents(
      events,
      data?.[IDataKeys.calendar]?.[
        getCalendarCacheKey(band, start, stop)
      ] as unknown as IJSONAPIItem[]
    );
  });

const dataEventSelector = createSelector(dataSelector, (data) => data.event);

export const eventSelectorCreator = (id: string) =>
  createSelector(dataEventSelector, (events) => {
    const event = events?.[API_ENDPOINTS.EVENT(id)];

    if (event) {
      return buildSingleItem(event) as IEvent;
    }
    return null;
  });

export const eventMetaSelector = (id: string) =>
  dataMetaSelectorHelper(BAND_ACTIONS.getEvent);

export const icalSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.ical)?.[0] as IBandIcal
);
